import React, { useState } from "react";
import { DatePicker } from "@mantine/dates";
import { IconTrash, IconCircleCheck, IconPencil } from "@tabler/icons";
import { Select, TextInput, Text } from "@mantine/core";
import _ from "lodash";

import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import ActionCellRender from "./actionCellRender";

import { DATA_COUNTRY, DATA_ID_TYPE } from "../data/options";
import { useCellRender } from "../hooks/useCellRender";
import { useForceUpdate } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { applyDefaultIdIssueCountry } from "../helpers/clientIdIssueCountry";
import {
  buildOtherIdRowValidate,
  isIdExpiryRequired,
  promoteIdentityToMain,
} from "../helpers/clientIdentity";

const ClientIdentityList = ({
  form: mainForm,
  name = "otherIds",
  hideTitle = false,
  ...props
}) => {
  const { t } = useTranslation();
  const otherIds = _.get(mainForm.values, name) ?? [];
  const [cellRender] = useCellRender();
  const [isPromoting, setIsPromoting] = useState(false);
  const forceUpdate = useForceUpdate();

  const tableSchema = {
    hidePagination: true,
    columns: [
      {
        field: "idType",
        headerName: "Type",
        cellRender: (col, data) => data.idType ?? "-",
      },
      {
        field: "id",
        headerName: "ID No.",
        cellRender: (col, data) => data.id ?? "-",
      },
      {
        field: "idIssueCountry",
        headerName: "Issue Country",
        cellRender: (col, data) => data.idIssueCountry ?? "-",
      },
      {
        field: "idDocExpiryDate",
        headerName: "Expiry",
        cellRender: (col, data) =>
          data.idType === "HKID"
            ? "-"
            : cellRender.Date(data.idDocExpiryDate, "YYYY-MM-DD") ?? "-",
      },
      {
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            label: "Edit",
            icon: IconPencil,
            props: {},
          },
          {
            name: "setMain",
            label: "Set as Main",
            icon: IconCircleCheck,
            props: {
              disabled: isPromoting,
            },
          },
          {
            name: "delete",
            label: "Delete",
            icon: IconTrash,
            props: {},
          },
        ],
      },
    ],
    searchableFields: [],
  };

  const formSchema = {
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "800px",
    },
    initialValues: {
      idType: "HKID",
      id: "",
      idIssueCountry: "Hong Kong",
      idDocExpiryDate: null,
    },
    validate: buildOtherIdRowValidate(t),

    layout: {
      containers: [
        {
          key: "section-other-id",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Identity Document",
            description: "Please enter the identity document details",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
      ],
      fields: [
        {
          name: "idType",
          component: Select,
          parent: "section-other-id",
          props: {
            required: true,
            label: "ID Type",
            data: DATA_ID_TYPE,
            defaultValue: "HKID",
            searchable: true,
            onDateChange: (value, form) =>
              applyDefaultIdIssueCountry(value, form),
          },
        },
        {
          name: "id",
          component: TextInput,
          parent: "section-other-id",
          props: {
            required: true,
            label: "ID No.",
            placeholder: "HKID / PASSPORT / BR",
          },
        },
        {
          name: "idIssueCountry",
          component: Select,
          parent: "section-other-id",
          props: {
            required: true,
            label: "Issue Country",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
          },
        },
        {
          name: "idDocExpiryDate",
          component: DatePicker,
          type: "datePicker",
          parent: "section-other-id",
          visibleOnly: (formValues) => formValues.idType !== "HKID",
          props: {
            required: (formValues) => isIdExpiryRequired(formValues.idType),
            label: "Expiry",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
      ],
    },
  };

  const onFormSubmit = (values) => {
    mainForm.setFieldValue(name, values);
    forceUpdate();
  };

  const onActionBtnClick = async ({ action, rowIndex }) => {
    if (isPromoting) return;
    if (action !== "setMain") return;

    try {
      setIsPromoting(true);
      const nextValues = promoteIdentityToMain(mainForm.values, rowIndex);
      mainForm.setValues(nextValues);
      forceUpdate();
    } finally {
      setIsPromoting(false);
      forceUpdate();
    }
  };

  if (mainForm.values?.type === "CORPORATE") return null;

  return (
    <>
      {!hideTitle && (
        <Text size="sm" weight={500} mt="md" mb="xs">
          Other IDs
        </Text>
      )}
      <LocalPageList
        rows={otherIds}
        onFormSubmit={onFormSubmit}
        tableSchema={tableSchema}
        formSchema={formSchema}
        onActionBtnClick={onActionBtnClick}
        btnLabel="Add Other ID"
        {...props}
      />
    </>
  );
};

export default ClientIdentityList;
